/**
 * Metrics snapshots -> chart series (M4 contract 7). Pure helpers used by MetricsSeriesChart and
 * the groups tables: the three PO metrics per snapshot, series breaks (the `series_key` changed
 * between consecutive snapshots, so the points are not comparable) and apexcharts series with
 * the line cut at each break.
 */

/** The three per-video PO metrics with their field in `metrics.global.methods.<method>`. */
export const SERIES_METRICS = Object.freeze([
  {
    key: 'incoherences_detected_pct',
    label: 'Lies detected',
    field: 'incoherents_detected_pct',
    target: 70,
    direction: 'min',
    color: '#1976d2',
  },
  {
    key: 'false_positives_pct',
    label: 'False alarms',
    field: 'truthful_flagged_pct',
    target: 2,
    direction: 'max',
    color: '#e53935',
  },
  {
    key: 'correct_pct',
    label: 'Correct videos',
    field: 'correctness_pct',
    target: 70,
    direction: 'min',
    color: '#43a047',
  },
]);

export const DEFAULT_METHOD = 'computed_subtraction';

const metricByKey = new Map(SERIES_METRICS.map((metric) => [metric.key, metric]));

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function toMillis(value) {
  if (!value) return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Value of a PO metric in a snapshot (or a list summary). Reads the aliased flat fields first
 * (`incoherences_detected_pct`, as in `last_snapshot`), then the full `metrics.global.methods` block.
 * @param {object} snapshot
 * @param {string} metricKey one of SERIES_METRICS keys
 * @param {string} method 'computed_subtraction' | 'alert_major'
 * @returns {number|null}
 */
export function metricValue(snapshot, metricKey, method = DEFAULT_METHOD) {
  if (!snapshot) return null;
  const metric = metricByKey.get(metricKey);
  if (!metric) return null;
  const flat = toNumber(snapshot[metricKey]);
  if (flat !== null && (!snapshot.metrics || method === DEFAULT_METHOD)) return flat;
  const global = snapshot.metrics?.global || snapshot.global || snapshot.metrics || null;
  const methods = global?.methods || null;
  const block = methods?.[method] || null;
  const fromMethod = toNumber(block?.[metric.field]);
  if (fromMethod !== null) return fromMethod;
  if (flat !== null) return flat;
  return toNumber(global?.[metric.field]);
}

/** Target status for a metric value: 'ok' | 'ko' | 'none'. */
export function metricStatus(metricKey, value) {
  const metric = metricByKey.get(metricKey);
  const n = toNumber(value);
  if (!metric || n === null) return 'none';
  const meets = metric.direction === 'max' ? n <= metric.target : n >= metric.target;
  return meets ? 'ok' : 'ko';
}

export function metricStatusColor(status) {
  if (status === 'ok') return 'success';
  if (status === 'ko') return 'error';
  return 'grey';
}

/** Signed delta text: '+3.2 pts', '−1 pts', '0 pts', '—' when either side is missing. */
export function formatDelta(a, b, { digits = 1, unit = 'pts' } = {}) {
  const na = toNumber(a);
  const nb = toNumber(b);
  if (na === null || nb === null) return '—';
  const delta = Math.round((nb - na) * 10 ** digits) / 10 ** digits;
  const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
  return `${sign}${Math.abs(delta)} ${unit}`.trim();
}

/**
 * Sorts snapshots by `computed_at` and marks `series_break` on every snapshot whose
 * `series_key` differs from the previous one (the first snapshot is never a break). Existing
 * `series_break` flags from the API are kept when the keys agree.
 * @param {Array<object>} snapshots
 * @returns {Array<object>} new array of shallow copies
 */
export function markSeriesBreaks(snapshots) {
  const list = (Array.isArray(snapshots) ? snapshots : [])
    .filter(Boolean)
    .map((snapshot) => ({ ...snapshot }))
    .sort((a, b) => (toMillis(a.computed_at) || 0) - (toMillis(b.computed_at) || 0));
  list.forEach((snapshot, index) => {
    if (index === 0) {
      snapshot.series_break = false;
      return;
    }
    const previous = list[index - 1];
    const keyChanged =
      snapshot.series_key != null &&
      previous.series_key != null &&
      snapshot.series_key !== previous.series_key;
    snapshot.series_break = Boolean(snapshot.series_break) || keyChanged;
  });
  return list;
}

/**
 * Consecutive snapshots sharing a series (a new segment starts at each `series_break`).
 * @param {Array<object>} snapshots
 * @returns {Array<Array<object>>}
 */
export function splitSegments(snapshots) {
  const segments = [];
  markSeriesBreaks(snapshots).forEach((snapshot) => {
    if (!segments.length || snapshot.series_break) segments.push([]);
    segments[segments.length - 1].push(snapshot);
  });
  return segments;
}

/**
 * apexcharts series (one per metric) over a datetime x axis. The line is cut at every series
 * break by inserting a `null` point right before the breaking snapshot, which apexcharts renders
 * as a gap when `connectNullData` is off.
 * @param {Array<object>} snapshots
 * @param {{ method?: string, metrics?: string[] }} options
 * @returns {Array<{ name: string, key: string, color: string, data: Array<{ x: number, y: number|null,
 *   meta: object|null }> }>}
 */
export function buildApexSeries(snapshots, { method = DEFAULT_METHOD, metrics = null } = {}) {
  const ordered = markSeriesBreaks(snapshots);
  const wanted =
    Array.isArray(metrics) && metrics.length ? metrics : SERIES_METRICS.map((m) => m.key);
  return wanted
    .map((key) => metricByKey.get(key))
    .filter(Boolean)
    .map((metric) => {
      const data = [];
      ordered.forEach((snapshot, index) => {
        const x = toMillis(snapshot.computed_at) ?? index;
        if (snapshot.series_break && data.length) {
          data.push({ x: x - 1, y: null, meta: null });
        }
        data.push({
          x,
          y: metricValue(snapshot, metric.key, method),
          meta: {
            snapshot_id: snapshot._id ?? snapshot.id ?? null,
            batch: snapshot.batch ?? null,
            trigger: snapshot.trigger ?? null,
            series_key: snapshot.series_key ?? null,
            series_break: Boolean(snapshot.series_break),
            engine_versions:
              snapshot.series_inputs?.engine_versions ??
              snapshot.metrics?.scope?.engine_versions ??
              snapshot.engine_versions ??
              null,
            config: snapshot.metrics?.scope?.config ?? snapshot.config ?? null,
            computed_at: snapshot.computed_at ?? null,
          },
        });
      });
      return { name: metric.label, key: metric.key, color: metric.color, data };
    });
}

/** Human summary of what changed at a break: 'gestures 2.0.3 → 2.1.0 · coherence 1.4.2'. */
export function describeSeriesInputs(inputs) {
  if (!inputs || typeof inputs !== 'object') return '';
  const parts = [];
  if (inputs.gestures_major_minor) parts.push(`gestures ${inputs.gestures_major_minor}`);
  if (inputs.coherence_major_minor) parts.push(`coherence ${inputs.coherence_major_minor}`);
  if (inputs.engines_hash) parts.push(`config ${String(inputs.engines_hash).slice(0, 8)}`);
  const resolved = inputs.resolved_inputs;
  if (resolved && typeof resolved === 'object') {
    ['stt_provider', 'stt_model', 'llm_model', 'sagemaker_endpoint', 'ruleset_source'].forEach(
      (key) => {
        if (resolved[key]) parts.push(`${key.replace(/_/g, ' ')} ${resolved[key]}`);
      }
    );
  }
  return parts.join(' · ');
}
